import { lazy, Suspense, useState, useEffect } from 'react';
import { Switch, Route, Redirect, useLocation, useSearch } from 'wouter';
import { useAuth } from './lib/auth';
import { fetchMyFirms } from './lib/api';
import NotFound from './pages/NotFound';

// Lazy-loaded routes: each page (and its heavy deps — maps, pdf, xlsx) becomes an on-demand chunk
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const PropertyNew = lazy(() => import('./pages/PropertyNew'));
const BulkImport = lazy(() => import('./pages/BulkImport'));
const PropertyView = lazy(() => import('./pages/PropertyView'));
const DealView = lazy(() => import('./pages/DealView'));
const HowTo = lazy(() => import('./pages/HowTo'));
const Marketplace = lazy(() => import('./pages/Marketplace'));
const Landing = lazy(() => import('./pages/Landing'));
const AdminBrokerEmails = lazy(() => import('./pages/AdminBrokerEmails'));
const AdminActivity = lazy(() => import('./pages/AdminActivity'));
const AdminCrawlers = lazy(() => import('./pages/AdminCrawlers'));
const AdminInquiries = lazy(() => import('./pages/AdminInquiries'));
const PowerMap = lazy(() => import('./pages/PowerMap'));
const DeedRecordsMap = lazy(() => import('./pages/DeedRecordsMap'));
const DeedData = lazy(() => import('./pages/DeedData'));
const CompData = lazy(() => import('./pages/CompData'));
const CompDataIntel = lazy(() => import('./pages/CompDataIntel'));
const Upgrade = lazy(() => import('./pages/Upgrade'));
const FirmAdmin = lazy(() => import('./pages/FirmAdmin'));
const Ardmore = lazy(() => import('./pages/Ardmore'));
const Screening = lazy(() => import('./pages/Screening'));
const ScreeningReport = lazy(() => import('./pages/ScreeningReport'));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F0F2F5]">
      <div className="w-6 h-6 border-2 border-gray-300 border-t-castle-500 rounded-full animate-spin" />
    </div>
  );
}

function ProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  const { user, loading } = useAuth();
  const [loc] = useLocation();
  const search = useSearch();
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-slate-400 text-sm">Loading...</div>
      </div>
    );
  }
  if (!user) {
    // Preserve where the user was headed (path + query) so login can return them there
    const next = encodeURIComponent(loc + (search ? `?${search}` : ''));
    return <Redirect to={`/login?next=${next}`} />;
  }
  return <Component />;
}

// When served from a firm's branded admin subdomain (admin.<slug>.crecastle.com), the
// whole app is a firm portal: only the branded login and firm admin exist — the Castle
// landing/marketplace/signup are never reachable there.
function firmPortalSlug(): string | null {
  if (typeof window === 'undefined') return null;
  const m = window.location.hostname.match(/^admin\.([a-z0-9-]+)\.crecastle\.com$/i);
  return m ? m[1] : null;
}

// A firm-only user (a firm member who is NOT the Castle global admin) is white-labeled:
// they can only ever see /firm-admin — every other Castle route redirects there, so they
// never touch the Castle dashboard, marketplace, or branding.
function FirmScopeGate({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [loc] = useLocation();
  const [state, setState] = useState<{ loading: boolean; firmOnly: boolean }>({ loading: true, firmOnly: false });
  const portal = firmPortalSlug();

  useEffect(() => {
    if (!user) { setState({ loading: false, firmOnly: false }); return; }
    let alive = true;
    setState({ loading: true, firmOnly: false });
    fetchMyFirms()
      .then(({ firms, isGlobalAdmin }) => { if (alive) setState({ loading: false, firmOnly: firms.length > 0 && !isGlobalAdmin }); })
      .catch(() => { if (alive) setState({ loading: false, firmOnly: false }); });
    return () => { alive = false; };
  }, [user?.id]);

  // Firm portal subdomain: only /login and /firm-admin exist here.
  if (portal && !loc.startsWith('/firm-admin') && !loc.startsWith('/login')) {
    return <Redirect to={`/login?firm=${portal}`} />;
  }
  if (user && state.loading) return <PageLoader />;
  if (user && state.firmOnly && !loc.startsWith('/firm-admin')) return <Redirect to="/firm-admin" />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <FirmScopeGate>
      <Switch>
        {/* The marketplace is the front door; the underwriting pitch lives at /underwriting. */}
        <Route path="/" component={Marketplace} />
        <Route path="/underwriting" component={Landing} />
        <Route path="/login" component={Login} />
        <Route path="/signup" component={Signup} />
        <Route path="/dashboard">
          <ProtectedRoute component={Dashboard} />
        </Route>
        <Route path="/property/new">
          <ProtectedRoute component={PropertyNew} />
        </Route>
        <Route path="/bulk-import">
          <ProtectedRoute component={BulkImport} />
        </Route>
        <Route path="/property/:id">
          <ProtectedRoute component={PropertyView} />
        </Route>
        <Route path="/deal/:shareToken" component={DealView} />
        <Route path="/listing/:slug/:shareToken" component={DealView} />
        <Route path="/marketplace"><Redirect to={`/${window.location.search}`} replace /></Route>
        <Route path="/firm-admin">
          <ProtectedRoute component={FirmAdmin} />
        </Route>
        <Route path="/how-to">
          <ProtectedRoute component={HowTo} />
        </Route>
        <Route path="/admin/broker-emails">
          <ProtectedRoute component={AdminBrokerEmails} />
        </Route>
        <Route path="/admin/crawlers">
          <ProtectedRoute component={AdminCrawlers} />
        </Route>
        <Route path="/admin/inquiries">
          <ProtectedRoute component={AdminInquiries} />
        </Route>
        <Route path="/admin/activity">
          <ProtectedRoute component={AdminActivity} />
        </Route>
        <Route path="/power-map">
          <ProtectedRoute component={PowerMap} />
        </Route>
        <Route path="/deed-records-map">
          <ProtectedRoute component={DeedRecordsMap} />
        </Route>
        <Route path="/deed-data" component={DeedData} />
        <Route path="/intel" component={CompDataIntel} />
        <Route path="/comp-data" component={CompData} />
        {/* Property screening — the tool itself, admin-gated and never shared. */}
        <Route path="/screening">
          <ProtectedRoute component={Screening} />
        </Route>
        {/* Public by design: one property's scorecard, reached only by an unguessable token. */}
        <Route path="/screening-report/:token" component={ScreeningReport} />
        {/* Unlisted ranch flyer landing page — reached by QR code, excluded from search */}
        <Route path="/ardmore" component={Ardmore} />
        <Route path="/upgrade">
          <ProtectedRoute component={Upgrade} />
        </Route>
        <Route component={NotFound} />
      </Switch>
      </FirmScopeGate>
    </Suspense>
  );
}
